import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getTenantId } from '../utils/tenantUtils'

export interface UserCashbackPromotionConfig {
  promotionType: 'cashback'
  enabled: boolean
  settings?: {
    display?: Partial<UserCashbackDisplaySettings>
  }
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserCashbackDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  heroBadgeText: string
  withdrawalButtonText: string
  noticeText: string
  minWithdrawalAmount: number
  cashbackRateText: string
  promotionalMessage: string
  disabledTitle: string
  disabledMessage: string
  historyEmptyText: string
  showRateTable: boolean
  showExample: boolean
  promotionRoute: string
  promotionCard: UserCashbackPromotionCardSettings
}

export interface UserCashbackPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserCashbackDisplayPromotionConfig {
  promotionType: 'cashback_display'
  enabled: boolean
  settings?: Partial<UserCashbackDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserReferralDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  withdrawButtonText: string
  shareSectionTitle: string
  linkLabel: string
  copyButtonText: string
  shareDescription: string
  disabledTitle: string
  disabledMessage: string
  historyEmptyText: string
  hideZeroCommissionSegments: boolean
  promotionRoute: string
  promotionCard: UserReferralPromotionCardSettings
}

export interface UserReferralPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserReferralDisplayPromotionConfig {
  promotionType: 'referral_display'
  enabled: boolean
  settings?: Partial<UserReferralDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserManualReviewBonusPromotionCardSettings {
  imageUrl: string
  imageAlt: string
  badgeText: string
  title: string
  description: string
  buttonText: string
}

export interface UserManualReviewBonusDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  contactSectionTitle: string
  contactSectionSubtitle: string
  requirements: string[]
  disabledTitle: string
  disabledMessage: string
  promotionCard: UserManualReviewBonusPromotionCardSettings
}

export interface UserBirthdayDisplayPromotionConfig {
  promotionType: 'birthday_display'
  enabled: boolean
  settings?: Partial<UserManualReviewBonusDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserFacebookShareDisplayPromotionConfig {
  promotionType: 'facebook_share_display'
  enabled: boolean
  settings?: Partial<UserManualReviewBonusDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserMonthlyRewardDisplaySettings {
  pageTitle: string
  subtitle: string
  rowsPerTab: number
  promotionCard: UserMonthlyRewardPromotionCardSettings
}

export interface UserMonthlyRewardPromotionCardSettings {
  badgeText: string
  title: string
  description: string
  buttonText: string
  route: string
}

export interface UserMonthlyRewardTabSettings {
  enabled?: boolean
  title?: string
}

export interface UserMonthlyRewardPromotionConfig {
  promotionType: 'monthly_reward'
  enabled: boolean
  settings?: {
    display?: Partial<UserMonthlyRewardDisplaySettings>
    tabs?: Partial<Record<'cashback' | 'loss' | 'refer' | 'rank', UserMonthlyRewardTabSettings>>
  }
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserPromotionHubShortcutSettings {
  enabled: boolean
  sortOrder: number
  badgeText: string
  title: string
  description: string
  buttonText: string
  route: string
}

export type UserPromotionHubCardActionType = 'none' | 'campaign' | 'route' | 'url'

export interface UserPromotionHubManualCard {
  id: string
  enabled: boolean
  sortOrder: number
  badgeText: string
  title: string
  description: string
  imageUrl: string
  imageAlt: string
  buttonText: string
  actionType: UserPromotionHubCardActionType
  actionValue: string
  openInNewTab: boolean
  startsAt: string | null
  endsAt: string | null
}

export interface UserPromotionHubSectionSettings {
  enabled: boolean
  sortOrder: number
}

export interface UserPromotionHubDisplaySettings {
  pageTitle: string
  pageSubtitle: string
  heroBadgeText: string
  warningTitle: string
  warningMessagePrimary: string
  warningMessageSecondary: string
  featuredSectionTitle: string
  featuredSectionSubtitle: string
  campaignsSectionTitle: string
  campaignsSectionSubtitle: string
  emptyCampaignText: string
  receiveButtonText: string
  showCampaigns: boolean
  disabledTitle: string
  disabledMessage: string
  sections: {
    warning: UserPromotionHubSectionSettings
    featuredPromotions: UserPromotionHubSectionSettings
    shortcuts: UserPromotionHubSectionSettings
    campaigns: UserPromotionHubSectionSettings
  }
  manualCards: UserPromotionHubManualCard[]
  shortcuts: {
    cashback: UserPromotionHubShortcutSettings
    monthlyReward: UserPromotionHubShortcutSettings
    referral: UserPromotionHubShortcutSettings
    birthday: UserPromotionHubShortcutSettings
    facebookShare: UserPromotionHubShortcutSettings
  }
}

export interface UserPromotionHubDisplayPromotionConfig {
  promotionType: 'promotion_hub_display'
  enabled: boolean
  settings?: Partial<UserPromotionHubDisplaySettings>
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface UserPromotionConfigResponse<TConfig> {
  success: boolean
  data?: TConfig
  message?: string
}

async function fetchPromotionConfig<TConfig>(path: string): Promise<UserPromotionConfigResponse<TConfig>> {
  const tenantId = getTenantId()
  const res = await fetch(withApiBaseUrl(path), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid promotion config response' }))
  return data as UserPromotionConfigResponse<TConfig>
}

export async function getCashbackPromotionConfig(): Promise<UserPromotionConfigResponse<UserCashbackPromotionConfig>> {
  return fetchPromotionConfig<UserCashbackPromotionConfig>(`/api/v1/user-auth/promotion-configs/cashback?_t=${Date.now()}`)
}

export async function getCashbackDisplayPromotionConfig(): Promise<UserPromotionConfigResponse<UserCashbackDisplayPromotionConfig>> {
  return fetchPromotionConfig<UserCashbackDisplayPromotionConfig>(`/api/v1/user-auth/promotion-configs/cashback_display?_t=${Date.now()}`)
}

export async function getReferralDisplayPromotionConfig(): Promise<UserPromotionConfigResponse<UserReferralDisplayPromotionConfig>> {
  return fetchPromotionConfig<UserReferralDisplayPromotionConfig>(`/api/v1/user-auth/promotion-configs/referral_display?_t=${Date.now()}`)
}

export async function getBirthdayDisplayPromotionConfig(): Promise<UserPromotionConfigResponse<UserBirthdayDisplayPromotionConfig>> {
  return fetchPromotionConfig<UserBirthdayDisplayPromotionConfig>(`/api/v1/user-auth/promotion-configs/birthday_display?_t=${Date.now()}`)
}

export async function getFacebookShareDisplayPromotionConfig(): Promise<UserPromotionConfigResponse<UserFacebookShareDisplayPromotionConfig>> {
  return fetchPromotionConfig<UserFacebookShareDisplayPromotionConfig>(`/api/v1/user-auth/promotion-configs/facebook_share_display?_t=${Date.now()}`)
}

export async function getMonthlyRewardPromotionConfig(): Promise<UserPromotionConfigResponse<UserMonthlyRewardPromotionConfig>> {
  return fetchPromotionConfig<UserMonthlyRewardPromotionConfig>(`/api/v1/user-auth/promotion-configs/monthly_reward?_t=${Date.now()}`)
}

export async function getPromotionHubDisplayPromotionConfig(): Promise<UserPromotionConfigResponse<UserPromotionHubDisplayPromotionConfig>> {
  return fetchPromotionConfig<UserPromotionHubDisplayPromotionConfig>(`/api/v1/user-auth/promotion-configs/promotion_hub_display?_t=${Date.now()}`)
}
